import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Navbar.css";
import NEWLOGO from "../images/NEWLOGO.png";
import { PROJECT_LOCATIONS } from "../data/projectsData";

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [desktopActiveCity, setDesktopActiveCity] = useState("Madurai");
  const [mobileExpandedCity, setMobileExpandedCity] = useState("Madurai");
  const [isScrolled, setIsScrolled] = useState(false);

  const location = useLocation();
  const navRef = useRef(null);

  // Close menus on page navigation
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesOpen(false);
    setProjectsOpen(false);
    setMobileExpandedCity(null);
  }, [location.pathname]);

  // Elevation on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 15);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menus on outside click or Escape key
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
        setServicesOpen(false);
        setProjectsOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
        setServicesOpen(false);
        setProjectsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Prevent background scrolling when mobile drawer is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  // Auto-close mobile drawer on desktop resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const closeAll = () => {
    setMobileMenuOpen(false);
    setServicesOpen(false);
    setProjectsOpen(false);
    setMobileExpandedCity(null);
  };

  const toggleServicesDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setServicesOpen((prev) => !prev);
    setProjectsOpen(false);
  };

  const toggleProjectsDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setProjectsOpen((prev) => !prev);
    setServicesOpen(false);
  };

  const toggleMobileCity = (cityName, e) => {
    e.preventDefault();
    e.stopPropagation();
    setMobileExpandedCity((prev) => (prev === cityName ? null : cityName));
  };

  // Helper active states
  const isServicesActive =
    location.pathname === "/residential" ||
    location.pathname === "/commercial" ||
    location.pathname === "/services";

  const isProjectsActive = location.hash.includes("regional-projects") || location.pathname === "/gallery";

  // Get active city object for desktop flyout
  const activeCityData =
    PROJECT_LOCATIONS.find((c) => c.city === desktopActiveCity) ||
    PROJECT_LOCATIONS[0];

  return (
    <>
      <header
        ref={navRef}
        className={`main-navbar ${isScrolled ? "navbar-scrolled" : ""}`}
        role="banner"
      >
        <div className="navbar-container">
          {/* Brand Logo & No.1 Badge */}
          <div className="navbar-brand-group">
            <Link
              to="/"
              onClick={closeAll}
              className="navbar-brand-link"
              aria-label="VARK Builders Homepage"
            >
              <img
                src={NEWLOGO}
                alt="VARK Builders Private Limited"
                className="brand-logo-img"
              />
            </Link>

            {/* Deep Blue Highlight Badge */}
            <div
              className="brand-badge-pill"
              title="Recognized as the No. 1 Construction Company in Madurai & Southern Tamil Nadu"
            >
              <span className="badge-icon">★</span>
              <span className="badge-text">NO. 1 BUILDERS IN MADURAI</span>
            </div>
          </div>

          {/* Hamburger Menu Toggle (Screens below 1024px) */}
          <button
            type="button"
            className={`hamburger-toggle ${mobileMenuOpen ? "active" : ""}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="primary-nav"
          >
            <span className="hamburger-bar bar-top"></span>
            <span className="hamburger-bar bar-mid"></span>
            <span className="hamburger-bar bar-bot"></span>
          </button>

          {/* Primary Navigation Menu */}
          <nav
            id="primary-nav"
            className={`nav-menu-wrapper ${mobileMenuOpen ? "mobile-open" : ""}`}
            aria-label="Primary Navigation"
          >
            <ul className="nav-links-list">
              <li className="nav-item">
                <Link
                  to="/"
                  className={`nav-link ${location.pathname === "/" && !location.hash ? "active" : ""}`}
                  onClick={closeAll}
                >
                  Home
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  to="/about"
                  className={`nav-link ${location.pathname === "/about" ? "active" : ""}`}
                  onClick={closeAll}
                >
                  About Us
                </Link>
              </li>

              {/* MULTI-LEVEL ONGOING PROJECTS DROPDOWN (Tier 1: Cities -> Tier 2: Sub-locations) */}
              <li
                className={`nav-item dropdown-item projects-dropdown-item ${
                  projectsOpen ? "dropdown-expanded" : ""
                }`}
                onMouseEnter={() => {
                  if (window.innerWidth >= 1024) setProjectsOpen(true);
                }}
                onMouseLeave={() => {
                  if (window.innerWidth >= 1024) setProjectsOpen(false);
                }}
              >
                <button
                  type="button"
                  className={`nav-link dropdown-toggle-btn ${isProjectsActive ? "active" : ""}`}
                  onClick={toggleProjectsDropdown}
                  aria-expanded={projectsOpen}
                  aria-haspopup="true"
                >
                  <span>ONGOING PROJECTS</span>
                  <svg
                    className={`dropdown-chevron ${projectsOpen ? "rotated" : ""}`}
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>

                {/* Desktop Multi-Level Flyout / Mobile Nested Accordion */}
                <div
                  className={`projects-mega-menu ${projectsOpen ? "menu-visible" : ""}`}
                  role="region"
                  aria-label="Ongoing Projects by City and Area"
                >
                  {/* DESKTOP TWO-TIER FLYOUT LAYOUT */}
                  <div className="desktop-projects-layout">
                    {/* Tier 1: Regional Cities List */}
                    <div className="tier1-cities-panel">
                      <div className="panel-header">
                        <span className="panel-header-tag">REGIONAL LOCATIONS</span>
                        <span className="panel-header-count">{PROJECT_LOCATIONS.length} Cities</span>
                      </div>
                      <ul className="tier1-cities-list" role="menu">
                        {PROJECT_LOCATIONS.map((loc) => (
                          <li
                            key={loc.city}
                            role="none"
                            className={`tier1-city-item ${
                              desktopActiveCity === loc.city ? "city-active" : ""
                            }`}
                            onMouseEnter={() => setDesktopActiveCity(loc.city)}
                          >
                            {loc.route && loc.route !== "/" ? (
                              <Link
                                to={loc.route}
                                className="tier1-city-link"
                                onClick={closeAll}
                                role="menuitem"
                              >
                                <div className="city-info-group">
                                  <span className="city-pin-icon">📍</span>
                                  <div className="city-text">
                                    <span className="city-name">{loc.city}</span>
                                    <span className="city-region">{loc.region}</span>
                                  </div>
                                </div>
                                <div className="city-meta">
                                  <span className="city-badge">{loc.subLocations.length} Areas</span>
                                  <span className="city-arrow">&rsaquo;</span>
                                </div>
                              </Link>
                            ) : (
                              <a
                                href={`#regional-projects`}
                                className="tier1-city-link"
                                onClick={closeAll}
                                role="menuitem"
                              >
                                <div className="city-info-group">
                                  <span className="city-pin-icon">📍</span>
                                  <div className="city-text">
                                    <span className="city-name">{loc.city}</span>
                                    <span className="city-region">{loc.region}</span>
                                  </div>
                                </div>
                                <div className="city-meta">
                                  <span className="city-badge">{loc.subLocations.length} Areas</span>
                                  <span className="city-arrow">&rsaquo;</span>
                                </div>
                              </a>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Tier 2: Sub-locations Flyout Panel */}
                    <div className="tier2-sublocations-panel">
                      <div className="tier2-panel-header">
                        <div className="tier2-title-box">
                          <span className="tier2-city-title">{activeCityData.city} Projects</span>
                          <span className="tier2-badge">{activeCityData.badge}</span>
                        </div>
                        <p className="tier2-subtitle">
                          Active residential villas and turnkey constructions in {activeCityData.city}:
                        </p>
                      </div>

                      <div className="tier2-sublocations-grid">
                        {activeCityData.subLocations.map((sub) => (
                          <a
                            key={sub.slug}
                            href={`#regional-projects`}
                            className="sublocation-card"
                            onClick={closeAll}
                          >
                            <div className="subloc-top">
                              <span className="subloc-icon">🏡</span>
                              <span className="subloc-name">{sub.name}</span>
                            </div>
                            <span className="subloc-project">{sub.projectName}</span>
                            <div className="subloc-meta">
                              <span className="subloc-type">{sub.type}</span>
                              <span className="subloc-status">{sub.status}</span>
                            </div>
                          </a>
                        ))}
                      </div>

                      <div className="tier2-footer-action">
                        {activeCityData.route && activeCityData.route !== "/" ? (
                          <Link
                            to={activeCityData.route}
                            className="view-city-projects-btn"
                            onClick={closeAll}
                          >
                            View Dedicated {activeCityData.city} Page &rarr;
                          </Link>
                        ) : (
                          <a
                            href={`#regional-projects`}
                            className="view-city-projects-btn"
                            onClick={closeAll}
                          >
                            View All {activeCityData.city} Showcase &rarr;
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* MOBILE/TABLET ACCORDION TREE (< 1024px) */}
                  <div className="mobile-projects-accordion">
                    <div className="mobile-accordion-notice">
                      Browse Active Projects by City:
                    </div>
                    {PROJECT_LOCATIONS.map((loc) => {
                      const isExpanded = mobileExpandedCity === loc.city;
                      return (
                        <div key={loc.city} className="mobile-city-block">
                          <button
                            type="button"
                            className={`mobile-city-trigger ${isExpanded ? "expanded" : ""}`}
                            onClick={(e) => toggleMobileCity(loc.city, e)}
                          >
                            <span className="mobile-city-name">
                              📍 {loc.city} ({loc.subLocations.length})
                            </span>
                            <span className={`mobile-city-chevron ${isExpanded ? "open" : ""}`}>
                              ▾
                            </span>
                          </button>

                          <div className={`mobile-sublocations-drawer ${isExpanded ? "drawer-open" : ""}`}>
                            {loc.route && loc.route !== "/" && (
                              <div className="mobile-city-hub-cta">
                                <Link
                                  to={loc.route}
                                  className="mobile-city-hub-link"
                                  onClick={closeAll}
                                >
                                  ★ View Dedicated {loc.city} Page &rarr;
                                </Link>
                              </div>
                            )}
                            <ul className="mobile-sublocations-list">
                              {loc.subLocations.map((sub) => (
                                <li key={sub.slug} className="mobile-subloc-item">
                                  <a
                                    href={`#regional-projects`}
                                    className="mobile-subloc-link"
                                    onClick={closeAll}
                                  >
                                    <div className="subloc-dot"></div>
                                    <div className="subloc-content">
                                      <strong className="subloc-title">{sub.name}</strong>
                                      <span className="subloc-sub">{sub.projectName}</span>
                                      <span className="subloc-pill">{sub.status}</span>
                                    </div>
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </li>

              {/* SERVICES DROPDOWN */}
              <li
                className={`nav-item dropdown-item services-dropdown-item ${
                  servicesOpen ? "dropdown-expanded" : ""
                }`}
                onMouseEnter={() => {
                  if (window.innerWidth >= 1024) setServicesOpen(true);
                }}
                onMouseLeave={() => {
                  if (window.innerWidth >= 1024) setServicesOpen(false);
                }}
              >
                <button
                  type="button"
                  className={`nav-link dropdown-toggle-btn ${isServicesActive ? "active" : ""}`}
                  onClick={toggleServicesDropdown}
                  aria-expanded={servicesOpen}
                  aria-haspopup="true"
                >
                  <span>Services</span>
                  <svg
                    className={`dropdown-chevron ${servicesOpen ? "rotated" : ""}`}
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>

                <ul
                  className={`dropdown-submenu services-submenu ${
                    servicesOpen ? "submenu-visible" : ""
                  }`}
                  role="menu"
                  aria-label="Services Submenu"
                >
                  <li role="none">
                    <Link
                      to="/residential"
                      className={`dropdown-sublink ${location.pathname === "/residential" ? "active" : ""}`}
                      onClick={closeAll}
                      role="menuitem"
                    >
                      <span className="sublink-icon">🏡</span>
                      <div className="sublink-text">
                        <span className="sublink-title">Residential</span>
                        <span className="sublink-desc">Luxury Villas &amp; Homes</span>
                      </div>
                    </Link>
                  </li>
                  <li role="none">
                    <Link
                      to="/commercial"
                      className={`dropdown-sublink ${location.pathname === "/commercial" ? "active" : ""}`}
                      onClick={closeAll}
                      role="menuitem"
                    >
                      <span className="sublink-icon">🏢</span>
                      <div className="sublink-text">
                        <span className="sublink-title">Commercial</span>
                        <span className="sublink-desc">Complexes &amp; Offices</span>
                      </div>
                    </Link>
                  </li>
                  <li role="none">
                    <Link
                      to="/services"
                      className={`dropdown-sublink ${location.pathname === "/services" ? "active" : ""}`}
                      onClick={closeAll}
                      role="menuitem"
                    >
                      <span className="sublink-icon">🛠️</span>
                      <div className="sublink-text">
                        <span className="sublink-title">All Services</span>
                        <span className="sublink-desc">Turnkey &amp; Interiors</span>
                      </div>
                    </Link>
                  </li>
                </ul>
              </li>

              <li className="nav-item">
                <Link
                  to="/gallery"
                  className={`nav-link ${location.pathname === "/gallery" ? "active" : ""}`}
                  onClick={closeAll}
                >
                  Gallery
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  to="/contact"
                  className={`nav-link ${location.pathname === "/contact" ? "active" : ""}`}
                  onClick={closeAll}
                >
                  Contact
                </Link>
              </li>

              {/* CTA Quote Button */}
              <li className="nav-item nav-cta-item">
                <Link to="/contact" onClick={closeAll}>
                  <button type="button" className="nav-quote-btn">
                    REQUEST A QUOTE
                  </button>
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Mobile Drawer Backdrop */}
      {mobileMenuOpen && (
        <div
          className="navbar-mobile-backdrop"
          onClick={closeAll}
          aria-hidden="true"
        />
      )}
    </>
  );
}
